/**
 * Typed mirror of the colour variables in `app/styles.css`.
 * Used by the contrast test and the design page; `tokens.test.ts` asserts it
 * matches the stylesheet, so the two cannot drift.
 */

/** Raw layer: designer values plus shades derived from them for contrast (`--brand-*`). */
export const brand = {
  black: '#000000',
  white: '#ffffff',
  navy: '#002d4e',
  'navy-hover': '#045691',
  // White at 6%, 8% and 12% over navy, composited: the sidebar's hover, search field and active fills.
  'navy-tint-6': '#0f3a59',
  'navy-tint-8': '#143e5c',
  'navy-tint-12': '#1f4663',
  blue: '#0088eb',
  'blue-link': '#0066b3',
  'blue-tint': '#e0f0fd',
  purple: '#942fae',
  'purple-tint': '#f6ebf9',
  yellow: '#ffb931',
  'yellow-hover': '#f2a612',
  amber: '#7a5200',
  'amber-tint': '#fff3d9',
  green: '#17703f',
  'green-tint': '#e4f4ea',
  danger: '#e63b20',
  'danger-strong': '#c42f17',
  'danger-hover': '#a82b18',
  'danger-tint': '#fdebe8',
  ground: '#e9f1f7',
  'grey-5': '#ecf3f8',
  'grey-10': '#dbe8f1',
  'grey-15': '#cbdbe6',
  'grey-40': '#809eb2',
  'grey-60': '#4f6a7c',
  'grey-70': '#3d4f5c',
  // Entity colours: a categorical set for the script review (boxes and type labels), one per entity type.
  'entity-1': '#0a67b5',
  'entity-1-tint': '#e3f0fb',
  'entity-2': '#b02a74',
  'entity-2-tint': '#fae8f2',
  'entity-3': '#0d7a5f',
  'entity-3-tint': '#e0f3ed',
  'entity-4': '#a8510a',
  'entity-4-tint': '#fcecdf',
  'entity-5': '#6a42c2',
  'entity-5-tint': '#efe9fb',
  'entity-6': '#7a6a00',
  'entity-6-tint': '#f4f0d6',
  // Revision paper colours: the swatch on a revision colour pill, hues matching the names (the label carries the tier).
  'revision-white': '#ffffff',
  'revision-blue': '#8ec5ef',
  'revision-pink': '#f29bbf',
  'revision-yellow': '#f2cc2e',
  'revision-green': '#8fcf8a',
  'revision-goldenrod': '#d9a21b',
  'revision-buff': '#ecd9b0',
  'revision-salmon': '#f4927a',
  'revision-cherry': '#c8284a',
  'revision-tan': '#c4a174',
  'revision-lavender': '#b39ddf',
} as const;

export type BrandToken = keyof typeof brand;

/** Semantic layer: what components use. Each maps to exactly one raw token. */
export const semantic = {
  background: 'ground',
  foreground: 'grey-70',
  heading: 'navy',
  card: 'white',
  'card-foreground': 'grey-70',
  popover: 'white',
  'popover-foreground': 'grey-70',
  primary: 'navy',
  'primary-hover': 'navy-hover',
  'primary-foreground': 'white',
  secondary: 'grey-10',
  'secondary-hover': 'grey-15',
  'secondary-foreground': 'navy',
  muted: 'grey-5',
  'muted-foreground': 'grey-60',
  accent: 'grey-5',
  'accent-foreground': 'navy',
  highlight: 'yellow',
  'highlight-hover': 'yellow-hover',
  'highlight-foreground': 'navy',
  emphasis: 'purple',
  'emphasis-foreground': 'white',
  destructive: 'danger-strong',
  'destructive-hover': 'danger-hover',
  'destructive-foreground': 'white',
  border: 'grey-15',
  input: 'grey-15',
  'input-fill': 'grey-5',
  ring: 'navy',
  scrim: 'navy',
  link: 'blue-link',
  'link-hover': 'navy',
  sidebar: 'navy',
  'sidebar-foreground': 'white',
  'sidebar-muted-foreground': 'grey-15',
  'sidebar-hover': 'navy-hover',
  'sidebar-item-active': 'navy-tint-12',
  'sidebar-item-hover': 'navy-tint-6',
  'sidebar-field': 'navy-tint-8',
  'sidebar-accent': 'grey-5',
  'sidebar-accent-foreground': 'navy',
  'sidebar-border': 'navy-hover',
  'sidebar-ring': 'yellow',
  'status-neutral': 'grey-70',
  'status-neutral-bg': 'grey-5',
  'status-info': 'blue-link',
  'status-info-bg': 'blue-tint',
  'status-progress': 'purple',
  'status-progress-bg': 'purple-tint',
  'status-success': 'green',
  'status-success-bg': 'green-tint',
  'status-warning': 'amber',
  'status-warning-bg': 'amber-tint',
  'status-danger': 'danger-hover',
  'status-danger-bg': 'danger-tint',
  'entity-1': 'entity-1',
  'entity-1-bg': 'entity-1-tint',
  'entity-2': 'entity-2',
  'entity-2-bg': 'entity-2-tint',
  'entity-3': 'entity-3',
  'entity-3-bg': 'entity-3-tint',
  'entity-4': 'entity-4',
  'entity-4-bg': 'entity-4-tint',
  'entity-5': 'entity-5',
  'entity-5-bg': 'entity-5-tint',
  'entity-6': 'entity-6',
  'entity-6-bg': 'entity-6-tint',
  'revision-white': 'revision-white',
  'revision-blue': 'revision-blue',
  'revision-pink': 'revision-pink',
  'revision-yellow': 'revision-yellow',
  'revision-green': 'revision-green',
  'revision-goldenrod': 'revision-goldenrod',
  'revision-buff': 'revision-buff',
  'revision-salmon': 'revision-salmon',
  'revision-cherry': 'revision-cherry',
  'revision-tan': 'revision-tan',
  'revision-lavender': 'revision-lavender',
} as const satisfies Record<string, BrandToken>;

export type SemanticToken = keyof typeof semantic;

/** Resolves a semantic token to its hex value. */
export function semanticHex(token: SemanticToken): string {
  return brand[semantic[token]];
}

/**
 * Revision swatches too close to a white card to show by themselves: the pill draws them with an outline. A swatch is
 * never the only signal (the pill names the colour), so the others need only be distinguishable, not AA.
 */
export const OUTLINED_REVISION_SWATCHES = ['white', 'buff'] as const;
export const REVISION_SWATCH_MINIMUM = 1.5;

/** WCAG 2.2 AA minimums: body text, and large text / UI components / focus indicators. */
export const AA_TEXT = 4.5;
export const AA_LARGE_OR_UI = 3;

export type ContrastPair = {
  foreground: SemanticToken;
  background: SemanticToken;
  minimum: typeof AA_TEXT | typeof AA_LARGE_OR_UI;
  use: string;
};

const text = (foreground: SemanticToken, background: SemanticToken, use: string): ContrastPair => ({
  foreground,
  background,
  minimum: AA_TEXT,
  use,
});
const ui = (foreground: SemanticToken, background: SemanticToken, use: string): ContrastPair => ({
  foreground,
  background,
  minimum: AA_LARGE_OR_UI,
  use,
});

/** Every foreground/background pair the system allows, with the ratio it must meet. */
export const contrastPairs: ContrastPair[] = [
  text('foreground', 'background', 'Body text on the page ground'),
  text('card-foreground', 'card', 'Body text on a card'),
  text('foreground', 'muted', 'Body text on a muted surface'),
  text('foreground', 'input-fill', 'Typed text in an input'),
  text('heading', 'background', 'Headings on the page ground'),
  text('heading', 'card', 'Headings on a card'),
  text('popover-foreground', 'popover', 'Menu and dialog text'),
  text('muted-foreground', 'background', 'Secondary text on the page ground'),
  text('muted-foreground', 'card', 'Secondary text on a card'),
  text('muted-foreground', 'muted', 'Secondary text and placeholders on a muted or input fill'),
  text('primary-foreground', 'primary', 'Primary button label'),
  text('primary-foreground', 'primary-hover', 'Primary button label, hovered'),
  text('secondary-foreground', 'secondary', 'Secondary button label'),
  text('secondary-foreground', 'secondary-hover', 'Secondary button label, hovered'),
  text('accent-foreground', 'accent', 'Hovered menu item, ghost button'),
  text('highlight-foreground', 'highlight', 'Accent (yellow) button label, avatar initial'),
  text('highlight-foreground', 'highlight-hover', 'Accent (yellow) button label, hovered'),
  text('emphasis-foreground', 'emphasis', 'Text on the purple accent'),
  text('destructive-foreground', 'destructive', 'Danger button label'),
  text('destructive-foreground', 'destructive-hover', 'Danger button label, hovered'),
  text('destructive', 'card', 'Error text on a card'),
  text('destructive', 'background', 'Error text on the page ground'),
  text('link', 'background', 'Link on the page ground'),
  text('link', 'card', 'Link on a card'),
  text('link', 'muted', 'Link on a muted surface'),
  text('link-hover', 'background', 'Hovered link'),
  text('sidebar-foreground', 'sidebar', 'Sidebar item'),
  text('sidebar-foreground', 'sidebar-hover', 'Sidebar item, hovered'),
  text('sidebar-muted-foreground', 'sidebar', 'Sidebar headings and secondary actions'),
  text('sidebar-foreground', 'sidebar-item-active', 'Active sidebar item'),
  text('sidebar-foreground', 'sidebar-item-hover', 'Sidebar item, hovered'),
  text('sidebar-muted-foreground', 'sidebar-item-hover', 'Sidebar secondary action, hovered'),
  text('sidebar-foreground', 'sidebar-field', 'Typed text in the sidebar search'),
  text('sidebar-muted-foreground', 'sidebar-field', 'Sidebar search placeholder and icon'),
  text('sidebar-accent-foreground', 'sidebar-accent', 'Initial on the org switcher'),
  text('status-neutral', 'status-neutral-bg', 'Neutral badge'),
  text('status-info', 'status-info-bg', 'Info badge'),
  text('status-progress', 'status-progress-bg', 'Progress badge'),
  text('status-success', 'status-success-bg', 'Success badge'),
  text('status-warning', 'status-warning-bg', 'Warning badge'),
  text('status-danger', 'status-danger-bg', 'Danger badge'),
  ui('ring', 'background', 'Focus ring on the page ground'),
  ui('ring', 'card', 'Focus ring on a card'),
  ui('ring', 'input-fill', 'Focus ring around an input'),
  ui('sidebar-ring', 'sidebar', 'Focus ring inside the sidebar'),
  ui('sidebar-ring', 'sidebar-item-active', 'Focus ring on the active sidebar item'),
  ui('primary', 'background', 'Primary button against the page ground'),
  ui('primary', 'card', 'Primary button against a card'),
  ui('destructive', 'card', 'Danger button against a card'),
  text('entity-1', 'entity-1-bg', 'Entity type label on its tint'),
  text('entity-2', 'entity-2-bg', 'Entity type label on its tint'),
  text('entity-3', 'entity-3-bg', 'Entity type label on its tint'),
  text('entity-4', 'entity-4-bg', 'Entity type label on its tint'),
  text('entity-5', 'entity-5-bg', 'Entity type label on its tint'),
  text('entity-6', 'entity-6-bg', 'Entity type label on its tint'),
  ui('entity-1', 'card', 'Selected reference box on the page'),
  ui('entity-2', 'card', 'Selected reference box on the page'),
  ui('entity-3', 'card', 'Selected reference box on the page'),
  ui('entity-4', 'card', 'Selected reference box on the page'),
  ui('entity-5', 'card', 'Selected reference box on the page'),
  ui('entity-6', 'card', 'Selected reference box on the page'),
];
